/**
 * Live RPG state for the local player: health with out-of-combat regen and
 * a tagged damage pipeline (built combat-ready before anything deals
 * damage), plus the inventory. Mutations bump `rev` — the UI re-renders
 * and the persistence layer saves off that single counter.
 */
import {
  DEFAULT_HEALTH,
  DEFAULT_MODE,
  ITEMS,
  SERVER_AUTHORITATIVE_HP,
  combatStats,
  consumeAt,
  cycleMode,
  defaultInventory,
  defaultUpgrades,
  type CombatMode,
  type CombatStats,
  type DamageSource,
  type HealthState,
  type InventoryState,
  type ItemDef,
  type MeleeDef,
  type UpgradeState,
} from "@hyperlight/shared";

/** How long a locally-PREDICTED hit stays subtracted from incoming server hp before we assume
 *  the server's stream already reflects it (≈ RTT + a broadcast tick, generous). */
const PREDICT_WINDOW_MS = 400;

/** Seconds after the last hit before regen kicks in. */
const REGEN_DELAY = 8;
const REGEN_RATE = 6; // hp/s

export class PlayerRpg {
  health: HealthState = { ...DEFAULT_HEALTH };
  inventory: InventoryState = defaultInventory();
  /** Bumped on every mutation — cheap dirty flag for UI + saves. */
  rev = 0;
  /** Server-authoritative balance (phase 8); display-only on the client. */
  credits = 0;
  /** One-time starter-cells grant flag (persisted with the inventory). */
  fuelKit = false;
  /** Called once when hp reaches 0; the game decides what death means. */
  onDeath: ((source: DamageSource) => void) | null = null;

  private sinceDamage = Infinity;

  /** Replace state from the persisted profile (login resume). */
  applyPersisted(health: HealthState, inventory: InventoryState): void {
    this.health = { ...health };
    this.inventory = inventory;
    this.sinceDamage = Infinity;
    this.rev++;
  }

  /** Fixed-tick: out-of-combat regen. Under SERVER_AUTHORITATIVE_HP the SERVER owns regen and
   *  streams it — a local loop would climb between messages and fight the authoritative bar. */
  update(dt: number): void {
    this.sinceDamage += dt;
    if (SERVER_AUTHORITATIVE_HP) return;
    const h = this.health;
    if (h.hp > 0 && h.hp < h.max && this.sinceDamage > REGEN_DELAY) {
      h.hp = Math.min(h.max, h.hp + REGEN_RATE * dt);
      this.rev++;
    }
  }

  damage(amount: number, source: DamageSource): void {
    const h = this.health;
    if (h.hp <= 0 || amount <= 0) return;
    h.hp = Math.max(0, h.hp - amount);
    this.sinceDamage = 0;
    this.rev++;
    if (h.hp === 0) this.onDeath?.(source);
  }

  // --- Client-side HP prediction (SERVER_AUTHORITATIVE_HP) ---------------------
  // The server owns hp and streams it ~RTT after a hit lands. For instant feel the client
  // PREDICTS damage it can see locally (bolt contact, melee cone, fall) and reconciles when
  // the authoritative value arrives. Predictions floor at 1 hp — only the server kills.

  /** Locally-applied damage the server hasn't confirmed yet. */
  private predicted: { amount: number; at: number }[] = [];

  /** Apply damage as a PREDICTION: instant bar drop, never lethal, never fires onDeath. */
  predictDamage(amount: number): void {
    const h = this.health;
    if (amount <= 0 || h.hp <= 0) return;
    // Shield-first (v50), mirroring the server's absorb order: the shield bar
    // dips instantly and the hp bar holds until it breaks.
    if (this.shield > 0) {
      const absorbed = Math.min(this.shield, amount);
      this.shield -= absorbed;
      amount -= absorbed;
      this.sinceDamage = 0;
      this.rev++;
      if (amount <= 0) return;
    }
    const applied = Math.min(amount, Math.max(0, h.hp - 1)); // the kill is the server's call
    this.sinceDamage = 0; // hurt vignettes/UI still key off recency
    if (applied <= 0) return;
    h.hp -= applied;
    this.predicted.push({ amount: applied, at: performance.now() });
    this.rev++;
  }

  /** Personal shield mirror (v50) — the pool is the SERVER's; this is what the
   *  HUD and the draw read. */
  shield = 0;
  shieldMax = 0;

  /** Adopt an authoritative hp update, keeping still-pending predictions subtracted so the
   *  bar doesn't pop back up while the server's stream catches up to hits it hasn't seen. */
  applyServerHealth(hp: number, max: number, dead: boolean, sh?: number, shMax?: number): void {
    const now = performance.now();
    this.predicted = this.predicted.filter((p) => now - p.at < PREDICT_WINDOW_MS);
    const pending = this.predicted.reduce((s, p) => s + p.amount, 0);
    this.health.max = max;
    this.health.hp = dead ? 0 : Math.min(max, Math.max(pending > 0 ? 1 : 0, hp - pending));
    this.shield = sh ?? 0;
    this.shieldMax = shMax ?? 0;
    this.rev++;
  }

  heal(amount: number): void {
    const h = this.health;
    if (amount <= 0 || h.hp <= 0 || h.hp >= h.max) return;
    h.hp = Math.min(h.max, h.hp + amount);
    this.rev++;
  }

  /** Full restore (respawn aboard the ship; phase-7 will add death costs). */
  restore(): void {
    this.health.hp = this.health.max;
    this.sinceDamage = Infinity;
    this.rev++;
  }

  /** Environmental shielding expires at this epoch-ms (Aegis Pack). */
  shieldUntil = 0;

  /** Seconds of hazard shielding remaining (0 = exposed). */
  shieldLeft(now: number): number {
    return Math.max(0, (this.shieldUntil - now) / 1000);
  }

  /** Use the consumable in `slot`. True if it did something. */
  useConsumable(slot: number): boolean {
    const s = this.inventory.slots[slot];
    const def = s ? ITEMS[s.id] : undefined;
    if (!def || def.kind !== "consumable") return false;
    if (def.heal !== undefined) {
      if (this.health.hp >= this.health.max || this.health.hp <= 0) return false;
      this.heal(def.heal);
    }
    if (def.shield !== undefined) {
      // Stacking extends from whatever is left — no waste.
      this.shieldUntil = Math.max(this.shieldUntil, performance.now()) + def.shield * 1000;
    }
    consumeAt(this.inventory, slot);
    this.rev++;
    return true;
  }

  // --- Weapons: ONE equipped weapon (inventory.primary points at it, server-mirrored
  // via the "equip" intent), DRAWN or STOWED with Z. The old Q/E construct modes
  // (cannon/shield/blade) are parked — `mode` stays only so old saves/tests load. ---

  /** DRAWN (armed) vs stowed. Toggled with Z; meaningless with nothing equipped. */
  armed = false;

  /** The equipped weapon's def, drawn or not. Null = nothing equipped (fists).
   *  The mainhand equip slot is the source of truth (weapons are DROPS, worn
   *  like armor — standard issue is retired); the legacy bag-slot pointer backs
   *  it up so a pre-v49 save still resolves. */
  equippedWeapon(): ItemDef | null {
    const hand = this.inventory.equipped.mainhand;
    if (hand && ITEMS[hand]) return ITEMS[hand];
    const i = this.inventory.primary;
    const s = i === null ? null : this.inventory.slots[i];
    return (s && ITEMS[s.id]) || null;
  }

  /** PARKED construct mode (pre-2026-08 combat). Unused by play; kept for save compat. */
  mode: CombatMode = DEFAULT_MODE;

  /** Purchased upgrade levels. SERVER-owned (bought with credits); the client
   *  mirrors what the account echo tells it. */
  upgrades: UpgradeState = defaultUpgrades();

  /** Every tuned number for the current upgrade levels. Recomputed on change,
   *  not per frame — the firing path reads this on every shot. */
  stats: CombatStats = combatStats(defaultUpgrades());

  /** Step the mode one place left (dir<0) or right (dir>0). Returns the new mode
   *  if it actually moved (the ends clamp), else null. */
  cycleMode(dir: number): CombatMode | null {
    const next = cycleMode(this.mode, dir);
    if (next === this.mode) return null;
    this.mode = next;
    this.rev++;
    return next;
  }

  /** Adopt the server's authoritative upgrade levels (account echo / login). */
  applyUpgrades(upgrades: UpgradeState): void {
    this.upgrades = { ...upgrades };
    this.stats = combatStats(this.upgrades);
    this.rev++;
  }

  /** The weapon IN HAND: equipped AND drawn, else null (unarmed). Drives rendering,
   *  firing, spread, the HUD and the `w` field on the wire. */
  heldWeapon(): ItemDef | null {
    return this.armed ? this.equippedWeapon() : null;
  }

  /** Back-compat name: the HELD weapon drives rendering/firing/HUD/network. */
  primaryWeapon(): ItemDef | null {
    return this.heldWeapon();
  }

  /** The held melee-class weapon's melee block, or null — gates the sword action layer. */
  heldMelee(): MeleeDef | null {
    return this.heldWeapon()?.weapon?.melee ?? null;
  }
}
